
import { Component, Vue, Watch } from 'vue-property-decorator';
import SAccUsersList from "@/components/users/SAccUsersList.vue";
import { createUser, deleteUser, fetchUsers, updateUser } from "@/axios/axios.user";
import { IUserQuotas, UserDocument, UserDocumentFlat, UserPayload } from "@/interfaces/user.interfaces";
import SAccUsersDetail from "@/components/users/SAccUsersDetail.vue";
import { addAuth, changeEmail, setIsAdmin } from "@/axios/axios.auth";
import { getIdTimestamp } from "@/shared/datetime.utils";

const emptyData: UserPayload = {
  firstname: '',
  lastname: '',
  email: '',
  isadmin: false,
  password: '',
  isActive: false,
  isDead: false,
  phone: '',
  picture: '',
  capacity: 'tester'
}

@Component({
  components: {SAccUsersDetail, SAccUsersList}
})
export default class SAccUsers extends Vue {
  // Les props
  // Les propriétés
  private loading: boolean = false
  private users: UserDocument[] = []
  private user: UserDocument | null = null
  private search: string = ''
  private editMode: boolean = false
  private addUser: boolean = false
  private payloadUser: UserPayload = {...emptyData}
  private isValid: boolean = false

  // Les propriétés calculées
  private get Quotas(): IUserQuotas {
    return this.$store.getters['userQuotas']
  }

  private get usedQuotas(): IUserQuotas {
    const uq: IUserQuotas = {
      tester: 0,
      guest: 0,
      certifier: 0
    }
    this.users.forEach(u => {
      const id = this.user ? this.user._id : ''
      const isActive = id === u._id ? this.payloadUser.isActive : u.isActive
      const capacity = id === u._id ? this.payloadUser.capacity : u.capacity
      if (isActive) {
        uq[(capacity as 'tester' | 'guest' | 'certifier')]++
      }
    })
    if (this.addUser) {
      uq.tester++
    }
    return uq
  }


  // Les hooks
  public mounted() {
    this.$store.commit('setWorkspace', 'User management')
    this.loadUsers()

  }

  // Les méthodes surveillées
  @Watch('user')
  private onChangeUser() {
    this.payloadUser = this.user ? {
      firstname: this.user.firstname,
      lastname: this.user.lastname,
      email: this.user.authid.email,
      isadmin: this.user.authid.isadmin,
      password: this.user.authid.password,
      isActive: this.user.isActive,
      isDead: this.user.isDead,
      phone: this.user.phone,
      picture: this.user.picture,
      capacity: this.user.capacity
    } : {...emptyData}
    this.editMode = false
  }

  // Les méthodes d'instance
  private async loadUsers() {
    this.loading = true
    this.users = await fetchUsers()
    this.loading = false
  }

  private onUpdate() {
    this.editMode = true
    this.addUser = false
  }

  private onCreate() {
    this.payloadUser = {...emptyData}
    this.editMode = true
    this.addUser = true
  }

  private async onSaveUser() {
    this.loading = true
    const body: Partial<UserPayload> = {...this.payloadUser}
    delete body.email
    delete body.password
    delete body.isadmin
    if (this.addUser && this.payloadUser.email) {
      const auth = await addAuth(this.payloadUser.email, this.payloadUser.isadmin)
      if (auth) {
        (body as UserDocumentFlat).authid = auth._id
        const u = await createUser(body)
        if (u) {
          u.createdAt = getIdTimestamp(u._id)
          u.authid = auth
          this.user = u
        }
      }
    } else if (this.user) {
      if (this.user.authid.email !== this.payloadUser.email) {
        await changeEmail(this.user.authid.email, this.payloadUser.email)
      }
      if (this.user.authid.isadmin !== this.payloadUser.isadmin) {
        await setIsAdmin(this.user.authid._id, this.payloadUser.isadmin)
      }
      this.user = await updateUser(this.user._id, body)
    }
    this.onChangeUser()
    await this.loadUsers()
    this.loading = false
    this.editMode = false
    this.addUser = false
  }

  private onCancel() {
    this.onChangeUser()
    this.editMode = false
    this.addUser = false
  }

  private async deleteUser() {
    if (this.user) {
      this.loading = true
      await deleteUser(this.user._id, this.user.authid._id)
      await this.loadUsers()
      this.user = null
    }
  }

  // Les méthodes statiques
}
